import React, { useState, useCallback, useContext } from "react";
import styled from "styled-components";
import CustomRadioButton from "../../Common/CustomRadioButton";
import CustomDropDown from "../../Common/CustomDropDown";
import { States } from "../../../utils/States";
import EditPic from "../../../SVGs/Edit.svg";
import {
  getApiClient,
  makeStandardApiErrorHandler,
} from "../../../server/get_api_client";
import toast from "react-hot-toast";
import Cookies from "js-cookie";
import { UserContext } from "../../../Context/user_context";
import CustomTextField from "../../Common/CustomTextField";
import PageLoader from "../../Common/PageLoader";

function CitizenshipSection({ data, contactId, refetchData }) {
  const [isLoading, setIsLoading] = useState(false);
  const { user, setUser } = useContext(UserContext);
  const [citizenshipInfo, setCitizenshipInfo] = useState(false);
  const [isUSCitizen, setIsUSCitizen] = useState(
    data ? data.hfa_uscitizen : true
  );
  const [usStateOfBirth, setUsStateOfBirth] = useState(
    data ? data.hfa_usstateofbirth : ""
  );
  const [citizenshipNotes, setCitizenshipNotes] = useState(
    data ? data.hfa_citizenshipnotes : ""
  );
  const [ssn, setSsn] = useState(data ? data.new_socialsecurity1 : "");
  // const [birthPlace, setBirthPlace] = useState(
  //   data ? data.hfa_usstateofbirth : ""
  // );

  const handleSsnChange = (value) => {
    const numericValue = value.replace(/\D/g, "");

    const limitedValue = numericValue.slice(0, 9);

    let formattedValue = "";
    if (limitedValue.length > 0) {
      formattedValue = limitedValue.slice(0, 3);
    }
    if (limitedValue.length > 3) {
      formattedValue += "-" + limitedValue.slice(3, 5);
    }
    if (limitedValue.length > 5) {
      formattedValue += "-" + limitedValue.slice(5, 9);
    }

    setSsn(formattedValue);
  };

  const handleCitizenshipInfo = useCallback(() => {
    setCitizenshipInfo(!citizenshipInfo);
  }, [citizenshipInfo]);

  const handleUpdateRecord = async () => {
    setIsLoading(true);
    const userid = contactId
      ? contactId
      : Cookies.get("hippocratic_portal_contactid");
    const recordData = {
      entity: "contacts",
      id: userid,
      attributes: [
        {
          fieldname: "hfa_uscitizen",
          value: isUSCitizen,
          type: "string",
          entity: null,
        },
        {
          fieldname: "hfa_usstateofbirth",
          value: usStateOfBirth,
          type: "optionset",
          entity: null,
        },
        {
          fieldname: "hfa_citizenshipnotes",
          value: citizenshipNotes,
          type: "string",
          entity: null,
        },
        {
          fieldname: "new_socialsecurity1",
          value: ssn,
          type: "string",
          entity: null,
        },
        // Only add the initial portal update flag if it hasn't been set yet
        ...(user.hip_initialportalinfoupdate
          ? []
          : [
              {
                fieldname: "hip_initialportalinfoupdate",
                value: "910310000",
                type: "optionset",
                entity: null,
              },
            ]),
      ],
    };
    try {
      await getApiClient().updateRecord(recordData);
      if (!user.hip_initialportalinfoupdate) {
        setUser({
          ...user,
          hip_initialportalinfoupdate: true,
        });
      }
      toast.success("Information has been updated succesfully");
      // Refetch data to get updated information
      if (refetchData) {
        await refetchData();
      }
    } catch (error) {
      makeStandardApiErrorHandler((error) => toast.error(error));
    } finally {
      setCitizenshipInfo(false);
      setIsLoading(false);
    }
  };

  return (
    <>
      <PageLoader isLoading={isLoading} />
      <Section>
        <TopPart>
          <SubTitle>Citizenship</SubTitle>
          {citizenshipInfo ? (
            <Save onClick={handleUpdateRecord}>Save Changes</Save>
          ) : (
            <Edit
              onClick={() => {
                handleCitizenshipInfo();
              }}
            >
              Edit
              <img style={{ height: "19px" }} src={EditPic} alt="edit-icon" />
            </Edit>
          )}
        </TopPart>
        <BottomPart>
          <CustomRadioButton
            title={"US Citizen:"}
            Active={citizenshipInfo}
            options={[true, false]}
            value={isUSCitizen}
            onChange={setIsUSCitizen}
          />
          {isUSCitizen && (
            <CustomDropDown
              Active={citizenshipInfo}
              options={States}
              Title="US State of Birth:"
              value={usStateOfBirth}
              onChange={setUsStateOfBirth}
            />
          )}
          {!isUSCitizen && (
            <CustomTextField
              Active={citizenshipInfo}
              Title="Birthplace:"
              value={citizenshipNotes}
              onChange={setCitizenshipNotes}
            />
          )}
          <CustomTextField
            Active={citizenshipInfo}
            Title="SSN:"
            placeholder="xxx-xx-xxxx"
            value={ssn}
            onChange={handleSsnChange}
          />
        </BottomPart>
      </Section>
    </>
  );
}

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  align-items: flex-start;
  justify-content: center;
`;

const TopPart = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
`;

const Edit = styled.div`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #87a9bf;
  font-family: "Museo Sans Cyrl";
  font-size: 16px;
  font-style: italic;
  font-weight: 300;
  line-height: 10px;
  text-decoration-line: underline;
`;

const BottomPart = styled.div`
  display: grid;
  gap: 30px;
  width: 100%;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  justify-items: start;

  @media screen and (max-width: 450px) {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  }
`;

const SubTitle = styled.div`
  color: #304961;
  font-family: "Museo Sans Cyrl";
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
`;

const CitizenshipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const Label = styled.div`
  font-family: "Museo Sans Cyrl";
  font-size: 16px;
  color: #304961;
`;

const Save = styled.div`
  border-radius: 300px;
  background: #304961;
  padding: 5px 10px;

  cursor: pointer;

  white-space: nowrap;

  color: #fff;
  text-align: center;
  font-family: "Museo Sans Cyrl";
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  text-transform: uppercase;
`;

export default CitizenshipSection;
